import type { ApiClient } from "@commerce-os/api-client";
import {
  confirmFileUpload,
  type FilePurpose,
  type FileView,
  getFile,
  requestFileUpload,
} from "@commerce-os/contracts";

export type UploadFile = (file: File, purpose: FilePurpose) => Promise<FileView>;

/**
 * The files flow (docs/specs/files.md) behind one call: ask the API for a signed URL, PUT the bytes straight to
 * storage, confirm, then wait while an image is processed. Resolves with the file once it is `ready`; a
 * rejected file (or one still processing after ~30 s) rejects.
 */
export function createUploader(api: ApiClient, fetchFn: typeof fetch, pollMs = 1000): UploadFile {
  return async (file, purpose) => {
    const target = await api.call(requestFileUpload, {
      body: { purpose, fileName: file.name, contentType: file.type, sizeBytes: file.size },
    });
    const put = await fetchFn(target.uploadUrl, { method: "PUT", headers: target.headers, body: file });
    if (!put.ok) throw new Error(`Upload failed with HTTP ${String(put.status)}`);
    let view = await api.call(confirmFileUpload, { params: { id: target.fileId } });
    for (let attempt = 0; view.status === "processing" && attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
      view = await api.call(getFile, { params: { id: target.fileId } });
    }
    if (view.status !== "ready") throw new Error(`The file is ${view.status}`);
    return view;
  };
}
